// What the approve dialog does about the sandbox gate (LO-191): the words that
// follow the kernel's own mode, the four refusals, the 202 that means "recorded,
// waiting for a re-run", and how a poll of the request is read.
//
// Plain functions and no React, so the rules are testable without a browser and
// the dialog stays about layout. Nothing here keeps a second copy of a decision
// the kernel makes. Whether a decision waits is the kernel's answer to the
// approve (202 or not); which mode the gate is in arrives on the request
// (`gate`) and on every response about it; a refusal's sentence is the kernel's
// `detail`, passed through whole. What lives here is the next step a person can
// take from each state (the kernel's own `next` is written for an API caller and
// names routes this screen does not call), and the order things are read in.
//
// The state machine this reads is `kernel/lopie_kernel/sandbox_gate.py`'s
// docstring, "Narrowing at approval".

/** What `POST .../approve` answers (202) and `approval_pending.state` says. */
export const WAITING_STATE = "approval_pending_rerun";

/** The four ways the gate refuses an approval, as the kernel names them. */
export const GATE_CODES = [
  "no_sandbox_run", "sandbox_run_failed", "sandbox_run_stale", "sandbox_not_ready",
];

/** Is this the 202 body: a decision recorded, nothing written? */
export function isWaiting(body) {
  return body?.state === WAITING_STATE;
}

// -- what striking does, said in the kernel's mode ----------------------------------

/**
 * What striking a line does, and what pressing Approve then does, for the mode
 * the kernel says the gate is in.
 *
 * `gate` is the request's own `gate` (`mode`, `enforced`, `applies`,
 * `narrowing_reruns`), or a response's. Four answers and no default that reads
 * as one of the others:
 *
 *  - **enforce**: striking changes the manifest a pass is bound to, so the
 *    narrowed manifest is re-tested and the approval waits for it.
 *  - **advisory**: the same checks run and nothing is blocked; striking does not
 *    re-run, and the approval lands as it always has.
 *  - **none**: the request names no build, so there is no sandbox and no gate.
 *  - **unknown**: the kernel did not say. Not read as either mode.
 *
 * `mode` is the word to put on the screen, never colour alone.
 */
export function gateWords(gate) {
  if (!gate || typeof gate !== "object" || !gate.mode) {
    return {
      mode: "unknown",
      word: "gate: not reported",
      striking: "This kernel did not say whether striking a line re-runs the sandbox, so nothing here assumes it does or does not.",
      approving: "This kernel did not say whether approving a narrowed manifest waits for a sandbox re-run.",
      problem: "",
    };
  }
  const problem = String(gate.configured_problem || "");
  if (gate.applies === false) {
    return {
      mode: "none",
      word: "no sandbox gate",
      striking: "This request names no build, so it has no sandbox to re-run and the gate holds nothing.",
      approving: "This request names no build, so approving it is not held on a sandbox run.",
      problem,
    };
  }
  if (gate.enforced) {
    return {
      mode: "enforce",
      word: "gate: enforce",
      striking: "Striking a line changes the manifest the sandbox passed, so the sandbox re-tests the narrowed manifest and your approval waits for that run to pass. Nothing is granted until then.",
      approving: "The gate is enforced, so Approve records this decision and the sandbox re-tests the narrowed manifest first. Nothing is granted until that run passes.",
      problem,
    };
  }
  return {
    mode: "advisory",
    word: "gate: advisory",
    striking: "Striking a line does not re-run the sandbox, and nothing is blocked: the narrowed manifest has no sandbox run of its own, and the gate only reports what it would have refused.",
    approving: "The gate is advisory, so Approve grants this at once. The narrowed manifest has no sandbox run of its own.",
    problem,
  };
}

// -- the four refusals ----------------------------------------------------------------

/** One failing check, as the kernel's `gate.failed_checks` carries it. */
function failedCheck(check) {
  return {
    id: String(check?.id || ""),
    title: String(check?.title || ""),
    persona: check?.persona ? String(check.persona) : "",
    route: check?.route ? String(check.route) : "",
    detail: String(check?.detail || ""),
  };
}

const NOT_READY = {
  run_in_progress: {
    title: "The sandbox run has not finished",
    next: "Wait for the run to finish, then approve again. The card's sandbox chip shows where it is.",
  },
  run_error: {
    title: "The sandbox run ended in an error",
    next: "That is the platform's fault and neither a pass nor a finding about the app. Another run has to be started for this manifest, which the author can do from Builder. Approve again once it has passed.",
  },
  not_provisioned: {
    title: "The sandbox has not been made yet",
    next: "It is made by itself from a request that names a build. Wait for it (the card's sandbox chip says when it is up), then approve again.",
  },
  repointing: {
    title: "The sandbox is being re-pointed",
    next: "It is being re-pointed and re-seeded for this manifest and cannot be run for it yet. Wait for that, then approve again.",
  },
  // LO-192: the kernel's `detail` carries the provider's own reason, and these say what to do about it.
  sandbox_stalled: {
    title: "The sandbox stalled and was replaced",
    next: "The platform made a second one of the same build when the first had not come up in time, and the replacement is coming up. A run starts by itself once it is ready: approve again then.",
  },
  sandbox_failed: {
    title: "The sandbox did not come up, twice",
    next: "It is not tried again, and waiting will not change that. That is the platform's fault and not a finding about the app. The author can save the draft again from Builder to get a new sandbox, or you can reject with a reason.",
  },
};

/**
 * A 409 from the approve, if it is the gate's, as something to render; else `null`.
 *
 * `detail` is the kernel's sentence, whole. `title` names the state in words and
 * `next` is the one thing a person here can do about it. `failed` is the failing
 * checks (only `sandbox_run_failed` carries them). `narrowed` is the kernel's own
 * statement that what was refused is the manifest as the approver narrowed it,
 * which changes the next step: approving exactly what was asked for is judged
 * against the run that got the request here, not against the narrowed one.
 *
 * Never a bare status and never "no results": a person who is told only that
 * approving failed cannot tell the sandbox is the reason, and nothing about what
 * to wait for.
 */
export function gateRefusal(error) {
  const status = Number(error?.status || 0);
  const code = String(error?.error || error?.body?.error || "");
  if (status !== 409 || !GATE_CODES.includes(code)) return null;
  const body = error?.body || {};
  const gate = body.gate || {};
  const narrowed = Boolean(gate.narrowed);
  const base = {
    code,
    detail: String(body.detail || error.message || ""),
    cause: String(gate.cause || ""),
    narrowed,
    runId: String(gate.run_id || ""),
    buildId: String(gate.build_id || ""),
    manifestHash: String(gate.manifest_hash || ""),
    failed: [],
  };
  if (code === "no_sandbox_run") {
    return {
      ...base,
      title: "No sandbox run exists for this build",
      next: "There is nothing to decide on yet. A run starts by itself once the sandbox is ready: watch the card's sandbox chip, and approve again when a run has finished. Rejecting is available at any time.",
    };
  }
  if (code === "sandbox_run_failed") {
    return {
      ...base,
      failed: (gate.failed_checks || []).map(failedCheck),
      title: "The newest sandbox run failed",
      next: narrowed
        ? "That run was for the manifest as you narrowed it. Strike fewer or different lines, or approve exactly what was asked for, which is judged against the run that got the request here. A failed run cannot be passed over: there is no override. Or reject with a reason."
        : "A failed run cannot be passed over: there is no override. The author has to fix the app and run it again, or you can reject with a reason.",
    };
  }
  if (code === "sandbox_run_stale") {
    return {
      ...base,
      title: "The sandbox has not run this manifest",
      next: "A pass belongs to one build and one manifest and does not carry across a change. The newest run is for a different one, so a run for this manifest has to finish first; the author can start one from Builder. Approve again once it has passed.",
    };
  }
  const known = NOT_READY[base.cause] || {
    title: "The sandbox cannot give a verdict yet",
    next: "Wait for the sandbox, then approve again.",
  };
  return { ...base, ...known };
}

// -- polling the request ---------------------------------------------------------------

/**
 * How long to wait before the next read of the request.
 *
 * The first read follows the answer by two seconds, then each wait is half again
 * as long as the one before, to a ceiling of fifteen. A re-run takes minutes (the
 * sandbox is re-pointed, re-seeded and QA-run), so a fixed short interval would
 * be a read of the request every two seconds for as long as an approver leaves a
 * window open, and every one of those reads is a call into a function that also
 * settles. `immediate` is for a window opened on a decision that has been waiting
 * a while: the first read is at once, and the backoff starts after it.
 */
export const POLL = Object.freeze({ first: 2000, factor: 1.5, max: 15000 });

export function pollDelay(attempt, { immediate = false } = {}) {
  const n = immediate ? attempt - 1 : attempt;
  if (n < 0) return 0;
  return Math.min(POLL.max, Math.round(POLL.first * POLL.factor ** n));
}

const CLOSED = ["rejected", "superseded", "cancelled"];

/**
 * What a read of `GET /manifest-requests/{id}` says about a decision that waits.
 *
 * `body` is the request view, with `approval_status` while a decision waits or on
 * the read that just settled it. Kinds, and whether polling goes on:
 *
 *  - **landed** (stop): `state` is `approved`. `viaThisRead` is true only when the
 *    kernel says this read is the one that wrote it (`outcome: completed`).
 *  - **waiting** (go on): `waitingFor` is the kernel's own object, with `run`
 *    (`run_id`, `status`) once a run exists for the narrowed manifest.
 *  - **failed** (stop): the re-run failed. The request is still pending and
 *    nothing is granted. `failure` names the run and the failing checks.
 *  - **blocked** (stop): the governor who decided no longer governs.
 *  - **overtaken** (stop): a newer decision, a rejection, a cancellation or a
 *    replacement ended the one that waited.
 *  - **settle_error** (go on): the run passed and the write was refused or failed;
 *    the decision is still recorded and reading again retries it.
 *  - **closed** (stop): the request is rejected, replaced or cancelled.
 *  - **no_decision** (stop): pending, and nothing waits. Not a state the kernel
 *    should leave a request in; said as that.
 *
 *  - **replaced** (stop): a different decision now waits on the request. There is
 *    one slot and a newer approve takes it, so this window's decision will not
 *    land. Judged from `decisionId`, the one this window recorded.
 *
 * `stop` is the one thing the poller needs to know.
 */
export function readProgress(body, { decisionId = "" } = {}) {
  const state = String(body?.state || "");
  const status = body?.approval_status || null;
  const pending = body?.approval_pending || null;

  if (state === "approved") {
    return {
      kind: "landed",
      stop: true,
      viaThisRead: status?.outcome === "completed",
      version: Number(body?.approved?.version || 0),
      by: String(body?.decided_by || ""),
      at: String(body?.decided_at || ""),
      detail: String(status?.detail || ""),
    };
  }
  if (CLOSED.includes(state)) {
    return {
      kind: "closed",
      stop: true,
      state,
      reason: String(body?.reason || ""),
      by: String(body?.decided_by || ""),
      supersededBy: String(body?.superseded_by || ""),
    };
  }
  if (state !== "pending") {
    return { kind: "no_decision", stop: true, state, detail: `The request is ${state || "in a state this screen does not know"}.` };
  }

  if (decisionId && pending?.decision_id && pending.decision_id !== decisionId) {
    return {
      kind: "replaced",
      stop: true,
      by: String(pending.decided_by || ""),
      at: String(pending.recorded_at || ""),
    };
  }
  const outcome = String(status?.outcome || "");
  if (outcome === "failed" || (!outcome && pending?.phase === "failed")) {
    return {
      kind: "failed",
      stop: true,
      failure: status?.failure || pending?.failure || null,
      detail: String(status?.detail || ""),
    };
  }
  if (outcome === "blocked" || (!outcome && pending?.phase === "blocked")) {
    return {
      kind: "blocked",
      stop: true,
      blocked: status?.blocked || pending?.blocked || null,
    };
  }
  if (outcome === "superseded") {
    return { kind: "overtaken", stop: true, detail: String(status?.detail || "") };
  }
  if (outcome === "error") {
    return {
      kind: "settle_error",
      stop: false,
      error: status?.error || null,
      detail: String(status?.detail || ""),
    };
  }
  if (outcome === "waiting" || pending) {
    return {
      kind: "waiting",
      stop: false,
      waitingFor: status?.waiting_for || null,
    };
  }
  return {
    kind: "no_decision",
    stop: true,
    detail: "The request is still pending and nothing waits on it: no decision is recorded.",
  };
}

/**
 * A sentence for what a waiting decision is waiting on right now, from the
 * kernel's `waiting_for`. The kernel's `reason` is the gate's code for the
 * narrowed manifest; `run` is present only when a run is bound to it.
 */
export function waitingOn(waitingFor) {
  const run = waitingFor?.run || null;
  const reason = String(waitingFor?.reason || "");
  if (run?.status === "running") {
    return `A sandbox run for the narrowed manifest is in progress (run ${run.run_id}).`;
  }
  if (run?.status === "error") {
    return `The run for the narrowed manifest (${run.run_id}) ended in an error. That is the platform's fault and neither a pass nor a finding about the app, and another run has to be started for it.`;
  }
  if (reason === "sandbox_not_ready") {
    return "The sandbox is being re-pointed and re-seeded for the narrowed manifest. A run starts by itself once it is ready.";
  }
  return "The sandbox is being pointed at the narrowed manifest. A run starts by itself once it is ready, and none has started yet.";
}

// -- an approval that landed after a re-run (LO-196) ----------------------------------------------

/**
 * The request's `follow_through` while it is open, or `null`.
 *
 * An approval that waited for its re-run lands without this window being open (the
 * kernel sweeps every minute), and the kernel writes a manifest and nothing else:
 * it does not release the build or install the app. `follow_through` is the
 * request saying so. It is what the kernel's landing did (neither), written once
 * when it landed, and it is not a report of the present: whether anyone has
 * released or installed since is a fact of the control plane, which
 * `followThroughLeft` reads.
 */
export function openFollowThrough(request) {
  const through = request?.follow_through;
  return request?.state === "approved" && through?.state === "open" ? through : null;
}

/**
 * What of the release and the install is left, from the control plane's own rows.
 *
 * `through` is `openFollowThrough(request)`; `build` is `GET /builds/{app}/{build}`
 * (its `published_version` is the release) or `null` if it could not be read;
 * `instances` is the control plane's list of installs (never a sandbox) or `null`
 * if it could not be read. Three answers for each of `released` and `installed`:
 * `true`, `false`, and `null` for "could not tell", which is never read as either,
 * because a notice that says "not installed" about an app that is running is a
 * worse answer than one that says it could not look.
 *
 * Installed is an install of this app that is not being removed, or one made since
 * the approval landed (an approver who installed it and then removed it has not
 * been left with an approval that is "not installed"). `done` is both `true`.
 */
export function followThroughLeft({ through, build, instances }) {
  if (!through) return { released: null, installed: null, done: true, unknown: false };
  const released = build ? Boolean(build.published_version) : null;
  let installed = null;
  if (Array.isArray(instances)) {
    const mine = instances.filter(
      (i) => i?.app === through.app_type && i.app !== "sandbox" && !i.sandbox,
    );
    // The two services write their clocks differently (the kernel's carries
    // microseconds and an offset, the control plane's ends in Z), so they are compared
    // as instants and never as strings.
    // Whole seconds, because that is all the control plane's clock has: an install made
    // in the second the approval landed would otherwise look as if it came before it.
    const parsed = Date.parse(through.landed_at || "");
    const since = Number.isNaN(parsed) ? parsed : Math.floor(parsed / 1000) * 1000;
    installed = mine.some((i) => i.desiredState !== "absent")
      || (!Number.isNaN(since) && mine.some((i) => Date.parse(i.createdAt || "") >= since));
  }
  return {
    released,
    installed,
    done: released === true && installed === true,
    unknown: released === null || installed === null,
  };
}

// -- the name given at approval, and the platform following it through (LO-202) ------------------

/** The control plane's `slug`, so the window only sends a name the kernel will record. */
function labelOf(displayName) {
  return String(displayName || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "").slice(0, 21).replace(/-+$/g, "");
}

/**
 * `{display_name}` for the approve body, or `null`.
 *
 * The approve window asks where the app will live, and the name goes with the approval
 * so the platform can release and install it however the approval lands, with the
 * window closed. A name that cannot be an address is not sent: the kernel would refuse
 * the whole approval over it, and the window already says the name is unusable.
 */
export function installFor(displayName) {
  const shown = typeof displayName === "string" ? displayName.trim() : "";
  const label = labelOf(shown);
  if (!shown || shown.length > 60 || !/^[a-z][a-z0-9-]{2,20}$/.test(label) || label === "prd") {
    return null;
  }
  return { display_name: shown };
}

/** The request's `follow_through` when the platform is following it through, or `null`. */
export function startedFollowThrough(request) {
  const through = request?.follow_through;
  return request?.state === "approved" && through?.state === "started" ? through : null;
}

/**
 * Whether the approve window releases the build and installs the app itself after an
 * approval lands. Not when the kernel recorded the name on the decision: the platform
 * does both from there, once, and the window doing them as well would be a second
 * caller racing the first (which the control plane refuses, but which is noise). Yes for
 * a kernel that recorded nothing, which is how every approval worked before LO-202.
 */
export function windowFollowsOn(body) {
  const request = body?.request || body;
  return !startedFollowThrough(request);
}

/**
 * What the card says about an approval the platform is following through.
 *
 * `through` is `startedFollowThrough(request)`. `record` is what the control plane's
 * `GET /follow-through/{request}` said: `undefined` not asked yet, `{unreadable}` it did
 * not answer, `{missing: true}` a 404 (nothing recorded yet), or the body (`claim`,
 * `progress`, `instance`). Returns `{phase, severity, title, detail, done, offerFinish}`
 * or `null` before the first answer. Never says "not installed" about a record it could
 * not read.
 */
export function followThroughSays(through, record) {
  if (!through || record === undefined) return null;
  const name = through.install?.name || "";
  const build = through.build_id;
  const base = { done: false, offerFinish: false, severity: "info" };
  if (record?.unreadable) {
    return {
      ...base, phase: "unknown", severity: "warning",
      title: "Approved. Whether the release and the install happened could not be checked",
      detail: `The control plane did not answer (${record.unreadable}). Nothing is assumed either way; this is read again on the next load.`,
    };
  }
  const progress = record?.progress || null;
  const instance = record?.instance || null;
  const status = instance?.status || "";
  const installed = (installName, by) => {
    const ready = status === "ready";
    return {
      ...base,
      phase: ready ? "ready" : "installed",
      severity: ready ? "success" : "info",
      done: ready,
      title: ready
        ? `Approved and installed as ${installName}`
        : `Approved and installed as ${installName}; it is ${status || "pending"}`,
      detail: by === "another caller, first"
        ? `Installed as ${installName} by whoever asked first, not under ${name}: one approval installs once, and the second name was refused.`
        : `Released and installed by the platform from the decision, under the name given at approval, with nobody pressing anything.${ready ? "" : " Ready is the composition's to say, usually within ten minutes."}`,
    };
  };
  if (!progress) {
    if (record?.claim?.name) return installed(record.claim.name, "");
    return {
      ...base, phase: "waiting",
      title: "Approved. The release and install have not started yet",
      detail: `It picks the approval up from the kernel's change feed within seconds, or on its sweep every two minutes, and installs it as ${name}.`,
    };
  }
  const version = progress.release?.version;
  switch (progress.state) {
    case "releasing":
      return { ...base, phase: "releasing", title: build ? `Approved. Releasing build ${build}` : "Approved. Releasing" };
    case "installing":
      return {
        ...base, phase: "installing",
        title: `Approved${version ? ` and released as version ${version}` : ""}; installing as ${name}`,
      };
    case "installed":
      return installed(progress.install?.name || record?.claim?.name || name, progress.install?.by);
    case "existing":
      return {
        ...base, phase: "existing", done: true, severity: "success",
        title: `Approved and released; ${through.app_type} is already installed as ${progress.install?.name}`,
        detail: progress.install?.detail || "It was not installed a second time.",
      };
    case "not_installable":
      return {
        ...base, phase: "none", done: true,
        title: `Approved; nothing to install: ${through.app_type} is not an app the catalog installs`,
        detail: progress.install?.detail || "",
      };
    case "failed": {
      const said = [progress.error, progress.install?.detail, progress.release?.detail]
        .filter(Boolean).join(". ");
      return {
        ...base, phase: "failed", severity: progress.retry ? "warning" : "error",
        offerFinish: !progress.retry,
        title: "Approved, and the release and install did not finish",
        detail: progress.retry
          ? `${said}. The platform tries again by itself (attempt ${progress.attempts || 1} of 5).`
          : `${said}. It will not be retried by itself; Release and install, with another name if the name was the problem, finishes it.`,
      };
    }
    default:
      return { ...base, phase: "unknown", title: `Approved. The follow-through says ${progress.state}` };
  }
}

/** The request id a body names, for a caller that has only the 202. */
export function requestIdOf(body) {
  return String(body?.request_id || body?.request?.request_id || "");
}

/** `user:josh@lopie.dev` as `josh@lopie.dev`; what the kernel stores, said as a person. */
export function whoIs(decidedBy) {
  const value = String(decidedBy || "");
  return value.startsWith("user:") ? value.slice("user:".length) : value;
}

/**
 * One declared aggregate as a sentence, from the fields the kernel sends.
 *
 * An aggregate is the declaration that reaches past the user half of the
 * intersection (LO-58), so what it computes has to be on screen next to the box
 * that strikes it: `count of Organization`, `sum(amount) of Invoice by category`.
 * `cohort` is the minimum contributing set, below which the kernel answers no
 * number at all.
 */
export function describeAggregate(aggregate) {
  const fn = String(aggregate?.function || "").trim();
  const field = String(aggregate?.field || "").trim();
  const what = fn ? `${fn}${field ? `(${field})` : ""}` : "an aggregate";
  const by = aggregate?.group_by ? ` by ${aggregate.group_by}` : "";
  const cohort = Number(aggregate?.cohort || 0);
  return `${what} of ${aggregate?.type || "an unnamed type"}${by}${cohort ? `, never for fewer than ${cohort} records` : ""}`;
}

/**
 * The aggregates that would be refused if the reads they compute over were
 * struck and they were kept: `parse_aggregates` refuses an aggregate over a type
 * the manifest does not declare in `reads`. Said before the button, because a
 * button the kernel is certain to refuse is a worse answer than a sentence.
 *
 * `keptTypes` is the set of types still ticked in the user reads.
 */
export function orphanedAggregates(aggregates, keptTypes, keptNames) {
  return (aggregates || []).filter(
    (a) => keptNames[a.name] && !keptTypes.has(a.type),
  );
}

/**
 * What approving a sync mapping also does for Insights (LO-213), said where the approver reads it.
 *
 * True because of how Insights is built, and it is stated here so a change to either side has a
 * test to break. A sync mapping can only write a built-in type today (`sync_maps.py`: a plan that
 * names an approved custom type is skipped), and Insights is granted every chartable built-in
 * type by its class, so no second manifest request is needed. What a person is then counted is
 * decided by their own tags, as it is everywhere. Turning the mapping off stops new records; the
 * ones already written stay until a run is rolled back, which is a separate act.
 */
export const SYNCMAP_CHARTABLE =
  "Once it has run, the records it writes can also be counted and charted in Insights by each " +
  "person who can open them. No second request is needed, and each person's own tags still " +
  "decide what they see. Turning the mapping off stops new records and does not remove the " +
  "ones already written.";
